import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
  ShopLogo,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@babackapp/customer-ui/components/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/customer-ui/components/select";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import {
  ArchiveXIcon,
  BoxIcon,
  LoaderIcon,
  SearchIcon,
  SlidersHorizontalIcon,
} from "lucide-react";
import { Controller } from "react-hook-form";
import { useOrderIdentificationForm, type OrderIdentificationMode } from "../hooks/use-order-identification-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useNavigate, useParams } from "react-router-dom";
import { LanguageSelector } from "./language-selector";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import {
  buildSecondaryInputIcon,
  buildSecondaryInputLabel,
} from "../utils/order-identification";

export function ReturnWidgetOrderIdentification() {
  const { form, mutation, onSubmit, logoUrl, isMerchant, shop, mode, setMode, validationInfo } = useOrderIdentificationForm();
  const navigate = useNavigate();
  const { shopId } = useParams<{ shopId: string }>();
  const shopName = shop.name;
  const t = useCustomText();

  const secondaryInputLabel = buildSecondaryInputLabel(validationInfo, t);
  const SecondaryInputIcon = buildSecondaryInputIcon(validationInfo);

  return (
    <WidgetPage variant="centered">
      <Header>
        <ShopLogo src={logoUrl ?? undefined} />
        <HeaderTitle>{t("orderIdentification.title", { shopName })}</HeaderTitle>
        <HeaderDescription>
          {t("orderIdentification.description", { shopName })}
        </HeaderDescription>
      </Header>

      <UserPortalCallout texts={shop.preferences?.userPortal?.headerMessage} />

      {mutation.error && (
        <Alert variant="destructive">
          <ArchiveXIcon />
          <AlertTitle>{t("orderIdentification.orderNotFound.title")}</AlertTitle>
          <AlertDescription>
            {t("orderIdentification.orderNotFound.description")}
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <Controller
          control={form.control}
          name="orderName"
          render={({ field }) => (
            <InputGroup className="h-auto">
              <InputGroupInput placeholder={`${shop.orderPrefix}00001`} {...field} />
              <InputGroupAddon align="block-start">
                <InputGroupText>{t("orderIdentification.fields.orderNumber")}</InputGroupText>
                <BoxIcon />
              </InputGroupAddon>
            </InputGroup>
          )}
        />
        {!isMerchant && (
          <Controller
            control={form.control}
            name="secondaryInput"
            render={({ field }) => (
              <InputGroup className="h-auto">
                <InputGroupInput placeholder={secondaryInputLabel} {...field} />
                <InputGroupAddon align="block-start">
                  <InputGroupText>{secondaryInputLabel}</InputGroupText>
                  <SecondaryInputIcon />
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        )}
        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            variants={{}}
          >
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? (
                <LoaderIcon className="animate-spin" />
              ) : (
                <>
                  <SearchIcon />
                  {t("orderIdentification.cta")}
                </>
              )}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
      {shop.giftCode?.activated && (
        <button
          type="button"
          className="font-content text-sm text-muted-foreground hover:text-foreground underline mt-2"
          onClick={() => navigate(`/return-widget/${shopId}/gift`)}
        >
          {t("gift.iReturnGift")}
        </button>
      )}
      <div className="flex items-center justify-between gap-4">
        <LanguageSelector />
        {isMerchant && (
          <ModeSelector
            value={mode}
            onChange={setMode}
            normalLabel={t("orderIdentification.mode.normal")}
            retractationLabel={t("orderIdentification.mode.retractation")}
          />
        )}
      </div>
    </WidgetPage>
  );
}

type ModeSelectorProps = {
  value: OrderIdentificationMode;
  onChange: (value: OrderIdentificationMode) => void;
  normalLabel: string;
  retractationLabel: string;
};

function ModeSelector({ value, onChange, normalLabel, retractationLabel }: ModeSelectorProps) {
  return (
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <SlidersHorizontalIcon className="size-3.5 shrink-0" />
      <Select value={value} onValueChange={(v) => onChange(v as OrderIdentificationMode)}>
        <SelectTrigger className="border-none shadow-none h-auto py-0 px-0 text-xs focus:ring-0 w-auto gap-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="normal" className="text-xs">{normalLabel}</SelectItem>
          <SelectItem value="retractation" className="text-xs">{retractationLabel}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

